import { FC, useMemo, useState } from 'react'

import styled from 'styled-components'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { ProperGenreOrLanguage, SelectedLanguage } from 'src/Types/tracks'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CollapsedSearchInput } from 'src/Components/CollapsedSearchInput'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'

import { LanguagesModalSpotifyButton } from './LanguagesModalSpotifyButton'
import { LanguagesModalSpotifyWrapperList } from './LanguagesModalSpotifyList'

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  padding-top: 23px;
  margin-bottom: 4px;
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
  line-height: 150%;
  margin-bottom: 4px;
`
const ListWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  overflow-x: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  height: 100%;
  padding-bottom: 50px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 0 24px 50px;
  }
`

const StyledModal = styled(FullScreenModal)`
  height: 100%;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  gap: 24px;
  padding-top: 22px;
  margin: 0 auto;
  background: ${({ theme }) => theme.color.white};
  position: sticky;
  top: 0;
  padding-bottom: 25px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    z-index: 2000;
  }
`

const StyledSearchInput = styled(CollapsedSearchInput)`
  max-width: 600px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  &.ant-input-affix-wrapper .ant-input-prefix {
    margin-inline-end: 8px;
  }
  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    border-radius: 40px;
    width: 100%;
    input::placeholder {
      visibility: visible;
    }
  }
  &:focus-visible,
  &:focus,
  &:focus-within {
    width: 100%;
    @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
      position: relative;
      width: 100%;
      input::placeholder {
        visibility: visible;
      }
    }
  }
  input::placeholder {
    color: ${({ theme }) => theme.color.slateGrey};
  }
`

const convertLanguages = (
  languages: ProperGenreOrLanguage[],
): SelectedLanguage | null => {
  const selected = languages.find((language) => language.id !== ANY_LANGUAGE_ID)

  if (!selected) {
    return null
  }

  return {
    value: Number(selected.id),
    label: selected.name,
  }
}

const getTargetLanguage = (languages: ProperGenreOrLanguage[]): number => {
  if (!languages.length) {
    return ANY_LANGUAGE_ID
  }

  const hasAnyLanguage = languages.some((item) => item.id === ANY_LANGUAGE_ID)

  return hasAnyLanguage ? ANY_LANGUAGE_ID : 0
}

export const LanguagesModalSpotifyWrapper: FC = () => {
  const { campaignDetails } = useSpotifyCampaignStatus()
  const { setSearch } = useCustomSearchParams()

  const campaignLanguages = useMemo(
    () => campaignDetails?.languages || [],
    [campaignDetails?.languages],
  )

  const [value, setValue] = useState('')
  const [localTargetLanguage, setLocalTargetLanguage] = useState<
    number | undefined
  >()
  const [localSelectedLanguage, setLocalSelectedLanguage] = useState<
    SelectedLanguage | null | undefined
  >()

  const targetLanguage = useMemo(
    () => localTargetLanguage ?? getTargetLanguage(campaignLanguages),
    [localTargetLanguage, campaignLanguages],
  )

  const selectedLanguage = useMemo(
    () =>
      typeof localSelectedLanguage === 'undefined'
        ? convertLanguages(campaignLanguages)
        : localSelectedLanguage,
    [localSelectedLanguage, campaignLanguages],
  )

  const handleClose = (): void => {
    setSearch({ modal: null })
  }

  const handleClick = (item: SelectedLanguage): void => {
    setLocalSelectedLanguage(
      selectedLanguage?.value === item.value ? null : item,
    )
  }

  return (
    <StyledModal
      showCloseButton
      onCancel={handleClose}
      prefixCls='languages'
      open
    >
      <ListWrapper>
        <Title>Your track language</Title>
        <SubTitle>
          Select the language sung or spoken in your track. This works as an
          additional parameter alongside your target genres to ensure the best
          playlist match.
        </SubTitle>
        <InputContainer>
          <StyledSearchInput
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setValue(e.target.value)
            }
            placeholder='Enter a language name'
          />
        </InputContainer>

        <LanguagesModalSpotifyWrapperList
          searchValue={value}
          setValue={setValue}
          selectedLanguage={selectedLanguage}
          targetLanguage={targetLanguage}
          onClick={handleClick}
          onChange={(id: number) => setLocalTargetLanguage(id)}
        />
      </ListWrapper>
      <LanguagesModalSpotifyButton
        targetLanguage={targetLanguage}
        selectedLanguage={selectedLanguage}
        onBackClick={handleClose}
        campaignLanguages={campaignLanguages}
      />
    </StyledModal>
  )
}
